
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Bot, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

export function WaitlistDialog({ 
  isOpen, 
  onClose 
}: { 
  isOpen: boolean; 
  onClose: () => void;
}) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("ai_agents_subscribers")
        .insert({
          email,
        });

      if (error) throw error;

      trackEvent("email_signup", { list: "ai_agents_waitlist" });
      setIsSubscribed(true);
      setEmail("");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "Failed to join waitlist. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setIsSubscribed(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={`text-2xl font-bold text-center mb-4 ${isSubscribed ? 'animate-fadeIn' : ''}`}>
            {isSubscribed ? "Welcome to the Waitlist!" : "Join the AI Agents Waitlist"}
          </DialogTitle>
          <DialogDescription className="text-center text-gray-600">
            {isSubscribed ? (
              "Thank you for joining our waitlist. We'll notify you when AI Agents launches!"
            ) : (
              "Enter your email address to join our waitlist and be notified when AI Agents launches."
            )}
          </DialogDescription>
        </DialogHeader>
        {isSubscribed ? (
          <div className="flex flex-col items-center space-y-4 animate-[fadeIn_0.5s,bounceIn_0.7s]">
            <div className="rounded-full bg-green-100 p-3 animate-bounce">
              <Bot className="h-6 w-6 text-green-600" />
            </div>
            <a
              href="https://landing.tixaeagents.ai/?aff=deepseek"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button
                className="w-full bg-[#9a00f7] hover:bg-[#9a00f7]/90 text-white animate-pulse shadow-[0_0_15px_rgba(154,0,247,0.5)] transition-shadow hover:shadow-[0_0_20px_rgba(154,0,247,0.8)]"
              >
                Try TIXAE Agents
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Button
              onClick={handleClose}
              variant="outline"
              className="w-full"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#0066FF] hover:bg-[#0066FF]/90 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Joining..." : "Join Waitlist"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
